import { EYE_HEIGHT } from '../panoramic/geometry';

/**
 * The LED ring, in metres: a band of screen bent into a full circle and hung at eye height in a
 * dark hall, its picture on the outside. The size is the work's own (its entry says how wide the
 * ring is and how tall the band); the film is repeated around it as many times as it takes to
 * keep the film's proportions, so a tile, a strip and a full panorama all sit on it undistorted.
 */
export interface RingGeometry {
  /** Centre to the lit face. */
  radius: number;
  /** The band's height. */
  height: number;
  /** Floor to the band's lower edge. */
  bottom: number;
  centreY: number;
  /** How many times the film goes around. */
  repeats: number;
  /** Flat facets per repeat; together they make the circle smooth. */
  segments: number;
}

export interface RingSize {
  diameter?: number;
  height?: number;
}

/** Circular Repetition, Venice 2019: six metres across, a band sixty centimetres tall. */
const DIAMETER = 6;
const HEIGHT = 0.6;

const within = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

export function ringGeometry(aspect: number, size: RingSize = {}): RingGeometry {
  const diameter = within(size.diameter || DIAMETER, 2, 16);
  const height = within(size.height || HEIGHT, 0.2, 5);
  // The band unrolled is this many times wider than tall; the film fits into it a whole number of times.
  const unrolled = (Math.PI * diameter) / height;
  const repeats = Math.max(1, Math.round(unrolled / within(aspect || 16 / 9, 0.2, 80)));
  // Hung with its middle at eye height; a tall band stops short of the floor instead.
  const bottom = Math.max(0.3, EYE_HEIGHT - height / 2);
  return {
    radius: diameter / 2,
    height,
    bottom,
    centreY: bottom + height / 2,
    repeats,
    segments: Math.max(2, Math.ceil(256 / repeats)),
  };
}

/** Where a visitor may walk: around the ring, never through its band, and not out of the hall. */
export function clampToRing(g: RingGeometry) {
  const inner = g.radius + 0.7;
  const outer = g.radius + 12;
  return (p: { x: number; z: number }) => {
    const d = Math.hypot(p.x, p.z);
    if (d < 1e-4) {
      p.z = inner;
      return;
    }
    const to = d < inner ? inner : d > outer ? outer : d;
    if (to !== d) {
      p.x *= to / d;
      p.z *= to / d;
    }
  };
}

const UNIT: Record<string, number> = { mm: 0.001, cm: 0.01, m: 1 };

/** One length out of an entry's dimensions line, in metres: "Diameter: 600cm" gives 6. */
function length(text: string, word: string): number | undefined {
  const found = text.match(new RegExp(`${word}[^0-9]{0,12}(\\d+(?:[.,]\\d+)?)\\s*(mm|cm|m)\\b`, 'i'));
  if (!found) return undefined;
  const metres = Number(found[1].replace(',', '.')) * UNIT[found[2].toLowerCase()];
  return Number.isFinite(metres) && metres > 0 ? metres : undefined;
}

/** The ring's size as its entry gives it ("Height: 60cm / Diameter: 600cm"); what is not said is left to the default. */
export function readRingSize(dimensions?: string | null): RingSize {
  if (!dimensions) return {};
  return { diameter: length(dimensions, 'diameter') ?? length(dimensions, 'Ø'), height: length(dimensions, 'height') };
}
